import type { authGlobalApiClient } from '@api-client/auth_global';
import type { ResType } from "@api-client/typeUtils";
import { IS_PROD } from '@lib/utils/getEnvVariable';
import { createAuthStorage } from './auth.idb';
import { acquireLock } from './auth.lock';

// Types
export type refreshAuthResultType = ResType<Awaited<typeof authGlobalApiClient['refresh']['$post']>>['data'];
export type AuthState = Exclude<refreshAuthResultType, { accessTokenPayload: { deviceId: string } }>

/**
 * Internal in-page event for token changes. SAFE because it is a same-document
 * CustomEvent — never a `postMessage` to a parent frame. The courier SPA listens
 * to this and forwards tokens to native via the Expo bridge. Iframed widgets
 * inside third-party parents simply ignore it.
 */
export const ZMG_AUTH_STATE_CHANGED_EVENT = 'zmg-auth:state-changed';


// Constants
const TOKEN_REFRESH_BUFFER_MS = IS_PROD ? 300_000 : 30_000; // prod 5 min before expiry, dev 30s (2 min access token)
const MAX_RETRY_ATTEMPTS = 3;
const REFRESH_TIMEOUT_MS = 15000; // 15s max wait

// Errors that indicate the session is completely invalid - must logout
const LOGOUT_ERROR_CODES = [
    'INVALID_REFRESH_TOKEN',
    'EXPIRED_REFRESH_TOKEN',
    'NO_REFRESH_TOKEN',
    'SESSION_FINGERPRINT_MISMATCH',
];

const isIframe = () => window.self !== window.top;

export class AuthManager {
    private _state: AuthState;
    private _listeners = new Set<(state: AuthState) => void>();
    private _refreshPromise: Promise<boolean> | null = null;
    private _waitingPromise: Promise<boolean> | null = null;
    private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private _readyResolve!: () => void;
    private _storage: ReturnType<typeof createAuthStorage<AuthState>>;
    private _channel: BroadcastChannel;

    readonly emptyState: AuthState = {
        accessToken: null,
        accessTokenPayload: null,
        refreshToken: null,
        refreshTokenPayload: null
    };

    readonly waitUntilReady: Promise<void>;

    readonly authState: {
        readonly value: AuthState;
        subscribe: (fn: (state: AuthState) => void) => () => boolean;
    };

    constructor() {
        this._state = this.emptyState;
        this._storage = createAuthStorage<AuthState>(this.emptyState);
        this._channel = new BroadcastChannel('ZMG_ALL_TABS_AND_IFRAMES_AUTH_CHANNEL');

        this.waitUntilReady = new Promise<void>(r => { this._readyResolve = r; });

        // Create authState with proper this binding
        const self = this;
        this.authState = {
            get value() { return self._state; },
            subscribe: (fn: (state: AuthState) => void) => {
                self._listeners.add(fn);
                return () => self._listeners.delete(fn);
            }
        };

        this._channel.onmessage = (e) => {
            // Only hydrate from broadcast, don't save back (sender already saved)
            this.hydrateState(e.data);
        };
    }

    setAuthState = async (state: refreshAuthResultType, broadcast = true) => {
        this.hydrateState(state as AuthState);
        await this._storage.save(state as AuthState);
        if (broadcast) this._channel.postMessage(state);
    };

    startRefreshingTokens = async (): Promise<boolean> => {
        // Return existing promise if already refreshing or waiting
        if (this._refreshPromise) return this._refreshPromise;
        if (this._waitingPromise) return this._waitingPromise;

        const unlock = await acquireLock('auth-refresh');
        // console.log('unlock?:', unlock ? '🟢' : '🔴');
        if (unlock) {
            // Double-check after await - another call might have started refresh
            if (this._refreshPromise) return this._refreshPromise;
            this._refreshPromise = this.refreshWithBackoff().finally(() => {
                this._refreshPromise = null;
                unlock();
            });
            return this._refreshPromise;
        }
        // Another tab is refreshing - wait for broadcast or timeout
        return this.waitForOtherTabRefresh();
    };

    private waitForOtherTabRefresh = (): Promise<boolean> => {
        if (!this._waitingPromise) {
            this._waitingPromise = new Promise<boolean>((resolve) => {
                const unsubscribe = this.authState.subscribe(() => {
                    unsubscribe();
                    clearTimeout(timeout);
                    resolve(!this.isAccessExpired());
                });
                const timeout = setTimeout(() => {
                    unsubscribe();
                    resolve(false);
                }, REFRESH_TIMEOUT_MS);
            }).finally(() => {
                this._waitingPromise = null;
            });
        }
        return this._waitingPromise;
    };

    getAccessToken = async (): Promise<string | null> => {
        // Wait for IDB to load before checking state
        await this.waitUntilReady;

        // Wait for any ongoing refresh or waiting promise
        const ongoingPromise = this._refreshPromise || this._waitingPromise;
        if (ongoingPromise) {
            await ongoingPromise;
            return this._state.accessToken;
        }

        if (!this.isAccessExpired()) return this._state.accessToken;
        if (this.isRefreshExpired()) return null;

        const success = await this.startRefreshingTokens();
        return success ? this._state.accessToken : null;
    };

    init() {
        if (typeof window === 'undefined') return;

        // Init from IndexedDB
        this._storage.load()
            .then(async (state) => {
                // Only hydrate if IDB has tokens (if empty, _state is already emptyState)
                // no need to hydrate if it same as emptyState
                if (state.accessTokenPayload) {
                    this.hydrateState(state);
                }

                setTimeout(() => {
                    this._readyResolve();
                    this.reloadUserIfExpired();
                });
            });

        if (new URLSearchParams(window.location.search).get('g_oauth_refresh_required')) {
            // SEARCH_ME: encrypted refresh token cookie with 1 minute expiry
            // flag to detect calling refresh
            this.startRefreshingTokens();
            // remove refresh required query param
            const url = new URL(window.location.href);
            url.searchParams.delete('g_oauth_refresh_required');
            window.history.replaceState({}, '', url.toString());
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.reloadUserIfExpired();
                this.scheduleAutoRefresh();
            } else {
                this.clearAutoRefresh();
            }
        });
    }

    // Private methods

    private hydrateState(state: AuthState) {
        this._state = state;
        this._listeners.forEach(fn => fn(state));
        this.scheduleAutoRefresh();
        // Same-document event so SPAs that opt in (e.g. the courier RN webview SPA)
        // can forward tokens to native. Never crosses origins.
        if (typeof document !== 'undefined') {
            try {
                document.dispatchEvent(
                    new CustomEvent(ZMG_AUTH_STATE_CHANGED_EVENT, { detail: state })
                );
            } catch {
                // CustomEvent unavailable in some non-browser envs — ignore.
            }
        }
    }

    private scheduleAutoRefresh = () => {
        this.clearAutoRefresh();
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        // auto refresh on top-level or data layer; skip other iframes (widget embeds)
        if (isIframe() && !(window as any).isDataLayer) return;
        if (!this._state.accessTokenPayload || this.isRefreshExpired()) return;

        const delay = this._state.accessTokenPayload.expiresAt - TOKEN_REFRESH_BUFFER_MS - Date.now();
        if (delay <= 0) return; // already expired, reloadUserIfExpired handles this

        this._refreshTimer = setTimeout(() => {
            console.log('auto refreshing tokens');
            this._refreshTimer = null;
            this.startRefreshingTokens();
        }, delay);
    };

    private clearAutoRefresh = () => {
        if (this._refreshTimer) {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = null;
        }
    };

    private isAccessExpired() {
        return !this._state.accessTokenPayload ||
            Date.now() >= (this._state.accessTokenPayload.expiresAt - TOKEN_REFRESH_BUFFER_MS);
    }

    private isRefreshExpired() {
        return !this._state.refreshTokenPayload ||
            Date.now() >= (this._state.refreshTokenPayload.expiresAt - TOKEN_REFRESH_BUFFER_MS);
    }

    private reloadUserIfExpired = async () => {
        if (!this.isAccessExpired()) return;
        if (!this.isRefreshExpired()) {
            await this.startRefreshingTokens()
        } else if (this._state.refreshTokenPayload) {
            // Only clear state if we HAD a refresh token that expired (not if we never had one)
            await this.setAuthState(this.emptyState)
        };
        // Otherwise, keep the state as is, bc we may override pkce tokens for multiple iframe widgets
    };

    private refreshWithBackoff = async (attempt = 0): Promise<boolean> => {
        try {
            const { authGlobalApiClient } = await import('../auth_global');
            const { data, error } = await (await authGlobalApiClient.refresh.$post()).json();
            if (error) {
                // Check if this is a fatal auth error that requires logout
                if (error.code && LOGOUT_ERROR_CODES.includes(error.code)) {
                    await this.setAuthState(this.emptyState);
                    return false;
                }
                // Another tab consumed the token first — wait for its broadcast with new tokens
                if (error.code === 'REFRESH_TOKEN_ALREADY_USED') {
                    return this.waitForOtherTabRefresh();
                }
                return false;
            }
            await this.setAuthState(data);
            return true;
        } catch {
            if (attempt < MAX_RETRY_ATTEMPTS) {
                await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
                return this.refreshWithBackoff(attempt + 1);
            }
            return false;
        }
    };
}
